import { completedCount, type DailyExercise } from '@echo/core';
import { useDayPart, useMe, useProgress, useToday } from '@echo/core/react';
import { select } from '@lingui/core/macro';
import { useLingui as useI18n } from '@lingui/react';
import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { ArrowLink } from '@/components/arrow-link';
import { ChooseExerciseSheet } from '@/components/choose-exercise-sheet';
import { ExerciseRow, ExerciseRowPlaceholders } from '@/components/exercise-row';
import { RoutineCompleteCard } from '@/components/routine-complete-card';
import { StreakCard } from '@/components/streak-card';
import { TodayProgressCard } from '@/components/today-progress-card';
import { WelcomeCard } from '@/components/welcome-card';
import { exerciseOpen, exerciseStart } from '@/lib/exercise-routes';

export const Route = createFileRoute('/_app/')({ component: HomePage });

/**
 * Home: the greeting with today's
 * date, the streak or welcome card, today's progress, then one "To do list" whatever the day
 * looks like — every row carries its own state — with a "Start exercise" link for what comes
 * next, and the celebration card under the list once all three are done. Extra rounds after
 * that count in stats and achievements; Home itself stays as it is.
 */
function HomePage() {
  const { t } = useLingui();
  const { i18n } = useI18n();
  const navigate = useNavigate();
  const part = useDayPart();
  const today = useToday();
  const progress = useProgress();
  const me = useMe();
  const name = me.data?.firstName;
  const date = i18n.date(new Date(), { weekday: 'long', month: 'long', day: 'numeric' });
  // Welcome card until the first exercise is done; from then on the streak card, even while
  // the streak is still 0. Until the sessions have loaded neither is known, so a blank card
  // holds the place — deciding early made the welcome flash and give way to the streak.
  const progressUnknown = progress.data === undefined && !progress.error;
  const firstRun = (progress.data?.exercisesDone ?? 0) === 0;
  const plan = today.data;
  const done = plan ? completedCount(plan) : 0;
  const total = plan?.exercises.length ?? 3;
  const exercises = plan?.exercises;
  // The routine continues with what is under way, else the next exercise not started yet.
  const next =
    exercises?.find((exercise) => exercise.status === 'in_progress') ??
    exercises?.find((exercise) => exercise.status === 'todo');
  const allDone = exercises !== undefined && next === undefined;
  const minutes = exercises?.[0]?.minutes ?? 0;
  const nextTitle = next?.title ?? '';
  // "Keep going" after a complete routine opens the chooser for another round.
  const [choosing, setChoosing] = useState(false);

  const openExercise = (exercise: DailyExercise) =>
    navigate(exerciseOpen(exercise.kind, exercise.status));
  const startExercise = (kind: DailyExercise['kind']) => navigate(exerciseStart(kind));

  return (
    <>
      <header className="flex flex-col">
        <h1 className="text-display-sm font-bold">
          {name
            ? t`${select(part, { morning: 'Good morning', afternoon: 'Good afternoon', other: 'Good evening' })} ${name}`
            : t`${select(part, { morning: 'Good morning', afternoon: 'Good afternoon', other: 'Good evening' })}`}
        </h1>
        <p className="text-body-sm text-on-surface-medium">{t`Today is ${date}`}</p>
      </header>

      <div className="flex flex-col gap-section-sm pt-content-md">
        {progressUnknown ? (
          <div
            className="h-40 rounded-xxl border border-border-weak bg-surface"
            role="progressbar"
            aria-label={t`Loading your progress`}
          />
        ) : firstRun ? (
          <WelcomeCard onStart={() => startExercise('reading')} />
        ) : (
          <StreakCard
            streak={progress.data?.streak ?? 0}
            todayIndex={(new Date().getDay() + 6) % 7}
            doneIndexes={progress.data?.doneIndexes ?? []}
          />
        )}

        <TodayProgressCard done={done} total={total} />

        {!exercises ? <ExerciseRowPlaceholders /> : null}

        {exercises ? (
          <section className="flex flex-col gap-content-sm">
            <div>
              <h2 className="text-title-lg">
                <Trans>To do list</Trans>
              </h2>
              <p className="text-body-lg text-on-surface-medium">
                <Trans>
                  Spend <Plural value={minutes} one="# minute" other="# minutes" /> for each
                  exercise
                </Trans>
              </p>
            </div>
            <ul className="flex flex-col gap-content-sm">
              {exercises.map((exercise) => (
                <li key={exercise.kind}>
                  <ExerciseRow exercise={exercise} />
                </li>
              ))}
            </ul>
            {/* Figma task-list: the trailing "Start exercise →" link opens what comes next. */}
            {next ? (
              <div className="flex justify-end">
                <ArrowLink aria-label={t`Start ${nextTitle}`} onClick={() => openExercise(next)}>
                  <Trans>Start exercise</Trans>
                </ArrowLink>
              </div>
            ) : null}
            {/* All three done: the celebration under the list; Keep going starts another round. */}
            {allDone ? <RoutineCompleteCard onKeepGoing={() => setChoosing(true)} /> : null}
          </section>
        ) : null}
      </div>

      {exercises ? (
        <ChooseExerciseSheet
          open={choosing}
          exercises={exercises}
          onClose={() => setChoosing(false)}
          onContinue={(exercise) => {
            setChoosing(false);
            void openExercise(exercise);
          }}
        />
      ) : null}
    </>
  );
}
